/**
 * Office Configuration
 * -------------------------------------------------------------------
 * Each office location gets its own config file. To add a new office,
 * create a new config object following this structure and import it
 * based on the URL path or subdomain (e.g., /wauwatosa, /tampa).
 *
 * The floor plan SVG coordinates define where each room appears on
 * the interactive map. The viewBox of the SVG is set by svgViewBox.
 *
 * Room email addresses correspond to M365 resource mailboxes.
 * -------------------------------------------------------------------
 */

export const OFFICE_CONFIGS = {
  wauwatosa: {
    id: 'wauwatosa',
    name: 'Wisconsin',
    fullName: 'SSP Innovations - Wisconsin Office',
    address: 'Wauwatosa, WI',
    svgViewBox: '110 100 1098 570',
    floorplanImage: '/MKE_FloorPlan.svg', // Architectural SVG used as background
    floorplanSize: { w: 1224, h: 792 },   // Full image dimensions (viewBox crops to visible area)

    // Room definitions - coordinates mapped to MKE_FloorPlan.svg
    rooms: [
      // Conference Room
      {
        id: 'lombardi',
        name: 'Lombardi Conference Room',
        type: 'conference',
        capacity: 20,
        email: 'mke.cr.lombardi@sspinnovations.com',
        floor: { x: 140, y: 182, w: 187, h: 140 },
      },
      // Meeting Room
      {
        id: 'starr',
        name: 'Starr Meeting Room',
        type: 'meeting',
        capacity: 10,
        email: 'mke.mr.starr@sspinnovations.com',
        floor: { x: 1065, y: 478, w: 113, h: 84 },
      },
      // Huddle Rooms
      {
        id: 'lambeau',
        name: 'Lambeau Huddle Room',
        type: 'huddle',
        capacity: 5,
        email: 'mke.hr.lambeau@sspinnovations.com',
        floor: { x: 210, y: 410, w: 98, h: 120 },
      },
      {
        id: 'holmgren',
        name: 'Holmgren Huddle Room',
        type: 'huddle',
        capacity: 5,
        email: 'mke.hr.holmgren@sspinnovations.com',
        floor: { x: 312, y: 410, w: 100, h: 120 },
      },
      // Offices O1-O6 (top row, sized to end at wall above C4)
      {
        id: 'O1',
        name: 'Office O1',
        type: 'office',
        capacity: 1,
        email: 'mke.o1@sspinnovations.com',
        floor: { x: 332, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O2',
        name: 'Office O2',
        type: 'office',
        capacity: 1,
        email: 'mke.o2@sspinnovations.com',
        floor: { x: 425, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O3',
        name: 'Office O3',
        type: 'office',
        capacity: 1,
        email: 'mke.o3@sspinnovations.com',
        floor: { x: 518, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O4',
        name: 'Office O4',
        type: 'office',
        capacity: 1,
        email: 'mke.o4@sspinnovations.com',
        floor: { x: 611, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O5',
        name: 'Office O5',
        type: 'office',
        capacity: 1,
        email: 'mke.o5@sspinnovations.com',
        floor: { x: 704, y: 182, w: 89, h: 140 },
      },
      {
        id: 'O6',
        name: 'Office O6',
        type: 'office',
        capacity: 1,
        email: 'mke.o6@sspinnovations.com',
        floor: { x: 797, y: 182, w: 89, h: 140 },
      },
      // O7 - Hannah Medaglia (non-bookable)
      {
        id: 'O7',
        name: 'Office O7',
        type: 'office',
        capacity: 1,
        bookable: false,
        assignedTo: 'H. Medaglia',
        mapLabel: 'Data Manager',
        floor: { x: 1065, y: 564, w: 113, h: 88 },
      },
      // Non-bookable rooms (shown on map only)
      {
        id: 'it-room',
        name: 'IT Room',
        type: 'utility',
        bookable: false,
        floor: { x: 928, y: 182, w: 80, h: 140 },
      },
      {
        id: 'mothers-room',
        name: "Mother's Room",
        type: 'utility',
        bookable: false,
        floor: { x: 1065, y: 395, w: 113, h: 80 },
      },
      // Cubicles C1-C8 (4 columns x 2 rows)
      { id: 'C1',  name: 'Cubicle C1',  type: 'cubicle', capacity: 1, email: 'mke.c1@sspinnovations.com',  floor: { x: 666, y: 355, w: 55, h: 62 } },
      { id: 'C2',  name: 'Cubicle C2',  type: 'cubicle', capacity: 1, email: 'mke.c2@sspinnovations.com',  floor: { x: 725, y: 355, w: 55, h: 62 } },
      { id: 'C3',  name: 'Cubicle C3',  type: 'cubicle', capacity: 1, email: 'mke.c3@sspinnovations.com',  floor: { x: 784, y: 355, w: 55, h: 62 } },
      { id: 'C4',  name: 'Cubicle C4',  type: 'cubicle', capacity: 1, email: 'mke.c4@sspinnovations.com',  floor: { x: 843, y: 355, w: 55, h: 62 } },
      { id: 'C5',  name: 'Cubicle C5',  type: 'cubicle', capacity: 1, email: 'mke.c5@sspinnovations.com',  floor: { x: 666, y: 423, w: 55, h: 62 } },
      { id: 'C6',  name: 'Cubicle C6',  type: 'cubicle', capacity: 1, email: 'mke.c6@sspinnovations.com',  floor: { x: 725, y: 423, w: 55, h: 62 } },
      { id: 'C7',  name: 'Cubicle C7',  type: 'cubicle', capacity: 1, email: 'mke.c7@sspinnovations.com',  floor: { x: 784, y: 423, w: 55, h: 62 } },
      { id: 'C8',  name: 'Cubicle C8',  type: 'cubicle', capacity: 1, email: 'mke.c8@sspinnovations.com',  floor: { x: 843, y: 423, w: 55, h: 62 } },
      // Cubicles C9-C20 (6 columns x 2 rows, aligned under C1-C8)
      { id: 'C9',  name: 'Cubicle C9',  type: 'cubicle', capacity: 1, email: 'mke.c9@sspinnovations.com',  floor: { x: 666, y: 505, w: 55, h: 62 } },
      { id: 'C10', name: 'Cubicle C10', type: 'cubicle', capacity: 1, email: 'mke.c10@sspinnovations.com', floor: { x: 725, y: 505, w: 55, h: 62 } },
      { id: 'C11', name: 'Cubicle C11', type: 'cubicle', capacity: 1, email: 'mke.c11@sspinnovations.com', floor: { x: 784, y: 505, w: 55, h: 62 } },
      { id: 'C12', name: 'Cubicle C12', type: 'cubicle', capacity: 1, email: 'mke.c12@sspinnovations.com', floor: { x: 843, y: 505, w: 55, h: 62 } },
      { id: 'C13', name: 'Cubicle C13', type: 'cubicle', capacity: 1, email: 'mke.c13@sspinnovations.com', floor: { x: 902, y: 505, w: 55, h: 62 } },
      { id: 'C14', name: 'Cubicle C14', type: 'cubicle', capacity: 1, email: 'mke.c14@sspinnovations.com', floor: { x: 961, y: 505, w: 55, h: 62 } },
      { id: 'C15', name: 'Cubicle C15', type: 'cubicle', capacity: 1, email: 'mke.c15@sspinnovations.com', floor: { x: 666, y: 573, w: 55, h: 62 } },
      { id: 'C16', name: 'Cubicle C16', type: 'cubicle', capacity: 1, email: 'mke.c16@sspinnovations.com', floor: { x: 725, y: 573, w: 55, h: 62 } },
      { id: 'C17', name: 'Cubicle C17', type: 'cubicle', capacity: 1, email: 'mke.c17@sspinnovations.com', floor: { x: 784, y: 573, w: 55, h: 62 } },
      { id: 'C18', name: 'Cubicle C18', type: 'cubicle', capacity: 1, email: 'mke.c18@sspinnovations.com', floor: { x: 843, y: 573, w: 55, h: 62 } },
      { id: 'C19', name: 'Cubicle C19', type: 'cubicle', capacity: 1, email: 'mke.c19@sspinnovations.com', floor: { x: 902, y: 573, w: 55, h: 62 } },
      { id: 'C20', name: 'Cubicle C20', type: 'cubicle', capacity: 1, email: 'mke.c20@sspinnovations.com', floor: { x: 961, y: 573, w: 55, h: 62 } },
    ],

    // Non-bookable areas (for visual context when no floorplan image)
    fixedAreas: [
      { label: 'Storage',           x: 78,   y: 328,  w: 80,  h: 40 },
      { label: 'Reception',         x: 133,  y: 370,  w: 85,  h: 30 },
      { label: 'Cafe',              x: 405,  y: 550,  w: 80,  h: 85 },
      { label: 'Back Entrance',     x: 680,  y: 290,  w: 120, h: 18 },
    ],
  },

  // TEMPLATE: Copy this block and fill in for a new office
  // tampa: {
  //   id: 'tampa',
  //   name: 'Tampa',
  //   fullName: 'SSP Innovations - Tampa Office',
  //   address: 'Tampa, FL',
  //   svgViewBox: '0 0 1200 600',
  //   rooms: [ ... ],
  //   fixedAreas: [ ... ],
  // },
};

/**
 * Get office config based on URL path or query param.
 * URL patterns supported:
 *   /wauwatosa or ?office=wauwatosa
 *   /tampa or ?office=tampa
 * Falls back to the first configured office.
 */
export function getOfficeConfig() {
  const params = new URLSearchParams(window.location.search);
  const officeParam = params.get('office');
  const pathSegment = window.location.pathname.split('/').filter(Boolean)[0];

  const officeId = officeParam || pathSegment || Object.keys(OFFICE_CONFIGS)[0];
  return OFFICE_CONFIGS[officeId] || OFFICE_CONFIGS[Object.keys(OFFICE_CONFIGS)[0]];
}
